import type { MidiClip, Project } from '@/domain/types';
import { isMidiClip } from '@/domain/types';
import type { CommandRecord } from '@/commands/types';
import { DRUM_PADS } from '@/engine/instruments/descriptors';
import { createId } from '@/util/id';
import { thinPoints } from '@/domain/curveThin';
import { normaliseParam, trackParamDescriptor, type ParamDescriptor } from '@/engine/effects/descriptors';
import { instrumentParamDescriptor } from '@/engine/instruments/descriptors';
import {
  bendPoints,
  modWheelPoints,
  holdPedalledNotes,
  pedalledNoteCount,
  performanceLanes,
  unmappedPerformance,
  type PerformancePoint,
} from './midiPerformance';
import { flattenTempoRamps, foldTempoRuns } from './midiTempo';
import { bendLaneToEvents, modWheelLaneToEvents, trackLaneToEvents } from './midiWheels';
import {
  encodeMidiFile,
  rescaleMidiFile,
  splitTracksByChannel,
  type MidiControllerEvent,
  type MidiFileInput,
  type MidiFileNote,
  type MidiPitchBendEvent,
  type MidiWritableTrack,
  type ParsedMidiFile,
} from '@/util/midiFile';

/**
 * Moving MIDI between Stage Zero and other software.
 *
 * Both directions are pure functions over data: import produces a list of
 * commands for the store to run, and export produces the structure the byte
 * writer takes. Neither touches the store, the audio engine or the DOM, which
 * is what lets the mapping decisions — which channel becomes a drum kit, where
 * a clip lands, whether the file's tempo is adopted — be tested directly.
 */

/** General MIDI reserves channel 10 (index 9) for percussion. */
const DRUM_CHANNEL = 9;

/**
 * How far a thinned controller ride may stray, as a fraction of the parameter's
 * range. A thousandth is far below what a fader move resolves and removes the
 * great majority of the points a real-time wheel produces.
 */
const PERFORMANCE_TOLERANCE = 0.001;

export interface MidiImportPlan {
  commands: CommandRecord[];
  /** Ids of the clips the import will create, for selecting them afterwards. */
  clipIds: string[];
  trackCount: number;
  noteCount: number;
  /** Automation points made from the file's controllers. */
  performancePoints: number;
  warnings: string[];
  /** True when the file's tempo and meter were taken as the project's. */
  adoptedTempo: boolean;
}

export interface MidiImportOptions {
  /** Timeline position the imported material starts at. */
  atTicks: number;
  /** Name shown on created tracks when the file does not name them. */
  fallbackName?: string;
}

/**
 * Turns a parsed file into the commands that would build it in a project.
 *
 * Nothing is applied here. Returning commands means the import is validated,
 * undoable and replayable by exactly the same machinery as every other edit —
 * and it can be inspected in a test without a project ever being mutated.
 */
export function planMidiImport(
  parsed: ParsedMidiFile,
  project: Project,
  options: MidiImportOptions,
): MidiImportPlan {
  const scaled = splitTracksByChannel(rescaleMidiFile(parsed, project.transport.ppq));
  const warnings = [...scaled.warnings];
  const commands: CommandRecord[] = [];
  const clipIds: string[] = [];

  // A file's tempo map is only adopted into an empty project. Overwriting the
  // tempo of work already in progress because a dropped file disagreed with it
  // would silently move every existing clip.
  const projectIsEmpty = Object.keys(project.clips).length === 0 && Object.keys(project.assets).length === 0;
  const adoptedTempo = projectIsEmpty && scaled.tempoEvents.length > 0;

  if (adoptedTempo) {
    // The whole tempo map, not just the opening tempo: a file that slows for
    // its final section is imported as a project that slows there too — and a
    // run of steps that is a ramp in a MIDI file's terms comes in as one ramp,
    // not as forty changes. See `foldTempoRuns`.
    for (const event of foldTempoRuns(scaled.tempoEvents, project.transport.ppq)) {
      commands.push({ type: 'transport.setTempo', params: { tick: event.tick, bpm: event.bpm } });
      if (event.ramp) {
        commands.push({
          type: 'transport.setTempoRamp',
          params: { tick: event.tick, ramp: true, shape: event.rampShape ?? 'linear' },
        });
      }
    }
    // The whole meter map comes across, not just the opening signature: a file
    // that moves to 7/8 for its bridge is imported as a project that does too.
    for (const signature of scaled.timeSignatures) {
      commands.push({
        type: 'transport.setTimeSignature',
        params: { tick: signature.tick, numerator: signature.numerator, denominator: signature.denominator },
      });
    }
  } else if (scaled.tempoEvents.length > 0) {
    const bpm = scaled.tempoEvents[0].bpm;
    const current = project.transport.tempoMap[0]?.bpm ?? 120;
    if (Math.abs(bpm - current) > 0.5) {
      warnings.push(
        `The file is at ${bpm.toFixed(1)} BPM and this project is at ${current.toFixed(1)}. ` +
          'The notes were imported at the project tempo; change the tempo if you want the file’s.',
      );
    }
  }

  if (!adoptedTempo && scaled.tempoEvents.length > 1) {
    warnings.push(
      'The file changes tempo part-way through. Its tempo map was not adopted, because this project already has work in it.',
    );
  }

  let noteCount = 0;
  let trackCount = 0;
  let performancePoints = 0;

  for (const track of scaled.tracks) {
    if (track.notes.length === 0) continue;

    const trackId = createId('trk');
    const clipId = createId('clp');
    const isDrums = track.channel === DRUM_CHANNEL;
    const name = track.name ?? `${options.fallbackName ?? 'MIDI'} ${trackCount + 1}`;

    // The pedal is applied to the notes on the way in, not carried as a lane:
    // a note released with the pedal down keeps sounding until it lifts. A drum
    // kit has no dampers, so its channel is read as written.
    const notes = isDrums ? track.notes : holdPedalledNotes(track);
    const pedalled = isDrums ? 0 : pedalledNoteCount(track);
    if (pedalled > 0) warnings.push(`${pedalled} note(s) in ${name} were held by the sustain pedal.`);

    const firstTick = Math.min(...notes.map((n) => n.startTicks));
    const lastTick = Math.max(...notes.map((n) => n.startTicks + n.durationTicks));
    const clipStart = Math.max(0, options.atTicks + firstTick);

    commands.push({ type: 'track.add', params: { kind: 'instrument', trackId, name } });
    // Channel 10 is percussion by long-standing convention, so a drum part
    // arrives on the drum machine rather than as a synth playing very low notes.
    if (isDrums) {
      commands.push({ type: 'instrument.set', params: { trackId, instrumentType: 'drumMachine' } });
    }
    commands.push({
      type: 'clip.addMidi',
      params: {
        clipId,
        trackId,
        name,
        startTicks: clipStart,
        durationTicks: Math.max(1, lastTick - firstTick),
      },
    });

    for (const note of notes) {
      commands.push({
        type: 'midi.addNote',
        params: {
          clipId,
          noteId: createId('not'),
          pitch: note.pitch,
          velocity: note.velocity,
          startTicks: note.startTicks - firstTick,
          durationTicks: note.durationTicks,
        },
      });
      noteCount++;
    }

    // The performance, as far as anything here can hold it.
    const lanes = performanceLanes(track);
    for (const [paramId, points] of [
      ['volume', lanes.volume],
      ['pan', lanes.pan],
    ] as const) {
      if (points.length === 0) continue;
      const laneId = createId('lan');
      commands.push({
        type: 'automation.addLane',
        params: { laneId, trackId, kind: 'track', paramId },
      });
      const descriptor = trackParamDescriptor(paramId);
      // Positioned like the clip, so a ride imported at bar nine rides the notes
      // it was written for rather than the start of the project.
      for (const point of thinnedRide(points)) {
        commands.push({
          type: 'automation.addPoint',
          params: {
            laneId,
            pointId: createId('pnt'),
            tick: Math.max(0, options.atTicks + point.tick),
            value: normalised(descriptor, point.value),
            curve: 'linear',
          },
        });
        performancePoints++;
      }
    }

    // Bend lives on the instrument rather than the track, because bending is
    // something the instrument does to its notes. A drum kit has nothing to
    // bend; everything else does.
    const bend = bendPoints(track);
    if (bend.length > 0 && !isDrums) {
      const laneId = createId('lan');
      commands.push({
        type: 'automation.addLane',
        params: { laneId, trackId, kind: 'instrument', paramId: 'bend' },
      });
      // The synth is what an imported melodic part lands on. Both it and the
      // sampler read `bend` over the same range, so the descriptor is the same
      // either way — but naming the instrument here rather than assuming it
      // keeps this honest if that stops being true.
      const descriptor = instrumentParamDescriptor('synth', 'bend');
      for (const point of thinnedRide(bend)) {
        commands.push({
          type: 'automation.addPoint',
          params: {
            laneId,
            pointId: createId('pnt'),
            tick: Math.max(0, options.atTicks + point.tick),
            value: normalised(descriptor, point.value),
            curve: 'linear',
          },
        });
        performancePoints++;
      }
    } else if (bend.length > 0) {
      warnings.push(
        `${name}: ${bend.length} pitch bend event(s) were not imported. A drum kit has nothing to bend.`,
      );
    }

    // The mod wheel is the instrument's too: it opens the synth's and the
    // sampler's filter. A drum kit has no filter for it to open.
    const modulation = modWheelPoints(track);
    if (modulation.length > 0 && !isDrums) {
      const laneId = createId('lan');
      commands.push({
        type: 'automation.addLane',
        params: { laneId, trackId, kind: 'instrument', paramId: 'modWheel' },
      });
      const descriptor = instrumentParamDescriptor('synth', 'modWheel');
      for (const point of thinnedRide(modulation)) {
        commands.push({
          type: 'automation.addPoint',
          params: {
            laneId,
            pointId: createId('pnt'),
            tick: Math.max(0, options.atTicks + point.tick),
            value: normalised(descriptor, point.value),
            curve: 'linear',
          },
        });
        performancePoints++;
      }
    } else if (modulation.length > 0) {
      warnings.push(
        `${name}: ${modulation.length} modulation event(s) were not imported. A drum kit has no filter for the wheel to open.`,
      );
    }

    for (const line of unmappedPerformance(track)) warnings.push(`${name}: ${line}`);

    clipIds.push(clipId);
    trackCount++;
  }

  if (trackCount === 0) warnings.push('This MIDI file contains no notes.');

  return { commands, clipIds, trackCount, noteCount, performancePoints, warnings, adoptedTempo };
}

/**
 * A controller ride, reduced to the points that describe it.
 *
 * A wheel recorded in real time sends an event every few milliseconds, so a
 * minute of riding is thousands of them — and a lane of thousands of points is
 * slower to schedule, slower to draw and no more accurate than the curve through
 * them. The same thinning an automation *recording* gets is applied here, for
 * exactly the same reason, and the tolerance is the same one.
 *
 * Points are already what the file said; nothing is resampled onto a grid.
 */
function thinnedRide(points: readonly PerformancePoint[]): PerformancePoint[] {
  return thinPoints(points, PERFORMANCE_TOLERANCE);
}

/**
 * An automation point's value, in the 0-to-1 an automation point actually holds.
 *
 * A lane stores every parameter the same way and the descriptor maps it back to
 * the parameter's own units, so a gain of 1 is *not* the number to write — for a
 * fader that runs to 1.6 it would mean full, and for a pan that runs from -1 it
 * would mean hard left. Nothing complains: the command clamps to 0..1 and the
 * import comes out confidently wrong.
 */
function normalised(descriptor: ParamDescriptor | undefined, value: number): number {
  return descriptor ? normaliseParam(descriptor, value) : value;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/** Flattens a clip's notes onto the timeline, expanding a looped clip. */
function flattenClip(clip: MidiClip): MidiFileNote[] {
  const out: MidiFileNote[] = [];
  const loopLength = clip.loop ? Math.max(1, clip.loopLengthTicks) : clip.durationTicks;
  const repeats = clip.loop ? Math.max(1, Math.ceil(clip.durationTicks / loopLength)) : 1;

  for (let repeat = 0; repeat < repeats; repeat++) {
    for (const note of clip.notes) {
      if (clip.loop && note.startTicks >= loopLength) continue;
      const start = clip.startTicks + repeat * loopLength + note.startTicks;
      if (start >= clip.startTicks + clip.durationTicks) continue;
      // A note is cut at the end of its clip, exactly as playback cuts it.
      const end = Math.min(start + note.durationTicks, clip.startTicks + clip.durationTicks);
      if (end <= start) continue;
      out.push({
        pitch: note.pitch,
        velocity: note.velocity,
        startTicks: start,
        durationTicks: end - start,
        channel: 0,
      });
    }
  }

  return out.sort((a, b) => a.startTicks - b.startTicks || a.pitch - b.pitch);
}

export interface MidiExportResult {
  file: MidiFileInput;
  trackCount: number;
  noteCount: number;
  /** Pitch bend and controller events written for the wheels and the track's volume and pan. */
  wheelEventCount: number;
}

/**
 * Collects a project's MIDI into the shape the byte writer takes.
 *
 * Loops are expanded and clip boundaries applied, so the file contains the
 * notes as they are *heard* rather than as they happen to be stored — which is
 * what another application will expect when it opens the result.
 */
export function projectToMidiFile(project: Project): MidiExportResult {
  const tracks: MidiWritableTrack[] = [];
  let noteCount = 0;
  let wheelEventCount = 0;
  let nextChannel = 0;

  for (const trackId of project.trackOrder) {
    const track = project.tracks[trackId];
    if (!track || track.kind !== 'instrument') continue;

    const notes = track.clipIds
      .map((clipId) => project.clips[clipId])
      .filter((clip): clip is MidiClip => Boolean(clip) && isMidiClip(clip) && !clip.muted)
      .flatMap(flattenClip);
    if (notes.length === 0) continue;

    const isDrums = track.instrument?.type === 'drumMachine';
    let channel: number;
    if (isDrums) {
      channel = DRUM_CHANNEL;
    } else {
      // Melodic parts skip channel 10, which every other application will read
      // as percussion.
      if (nextChannel === DRUM_CHANNEL) nextChannel++;
      channel = Math.min(15, nextChannel);
      nextChannel++;
    }

    // The wheels leave with the notes: a bend lane as pitch bend, with the
    // range it needs declared, and a mod wheel lane as CC1. A drum kit has
    // neither to carry. The track's own volume and pan lanes leave as CC7 and
    // CC10, the controllers the import reads them from — a kit's included.
    const controllers: MidiControllerEvent[] = [];
    const pitchBends: MidiPitchBendEvent[] = [];
    for (const laneId of track.automationLaneIds) {
      const lane = project.automation[laneId];
      if (!lane) continue;
      if (lane.target.kind === 'track') {
        controllers.push(...trackLaneToEvents(lane, project.transport.ppq, channel));
        continue;
      }
      if (lane.target.kind !== 'instrument' || isDrums) continue;
      if (lane.target.paramId === 'bend') {
        const written = bendLaneToEvents(lane, project.transport.ppq, channel);
        controllers.push(...written.declaration);
        pitchBends.push(...written.bends);
      } else if (lane.target.paramId === 'modWheel') {
        controllers.push(...modWheelLaneToEvents(lane, project.transport.ppq, channel));
      }
    }

    tracks.push({
      name: track.name,
      channel,
      notes: notes.map((note) => ({ ...note, channel })),
      controllers,
      pitchBends,
    });
    noteCount += notes.length;
    wheelEventCount += controllers.length + pitchBends.length;
  }

  return {
    file: {
      ppq: project.transport.ppq,
      name: project.meta.name,
      // A ramp leaves as a run of steps, because MIDI has no ramp.
      tempoEvents: flattenTempoRamps(project.transport.tempoMap, project.transport.ppq),
      timeSignatures: project.transport.timeSignatures.map((event) => ({
        tick: event.tick,
        numerator: event.numerator,
        denominator: event.denominator,
      })),
      tracks,
    },
    trackCount: tracks.length,
    noteCount,
    wheelEventCount,
  };
}

export function encodeProjectAsMidi(project: Project): {
  bytes: Uint8Array;
  trackCount: number;
  noteCount: number;
  wheelEventCount: number;
} {
  const result = projectToMidiFile(project);
  return {
    bytes: encodeMidiFile(result.file),
    trackCount: result.trackCount,
    noteCount: result.noteCount,
    wheelEventCount: result.wheelEventCount,
  };
}

/** The drum pitches the kit answers to, for reporting what an import will hit. */
export const DRUM_PITCHES: readonly number[] = DRUM_PADS.map((pad) => pad.pitch);
